import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, ArrowRight, Loader2, AlertCircle, CheckCircle2,
  Eye, Globe, TrendingUp, Award, BookOpen, Star,
} from "lucide-react";
import { CATEGORIAS_FILTRO } from "@/data/profissionaisData";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { postJSON } from "@/lib/api";

const BENEFICIOS = [
  { icon: Eye, title: "Visibilidade qualificada", desc: "Seja encontrado por famílias que buscam exatamente o que você oferece." },
  { icon: Globe, title: "Presença digital", desc: "Um perfil profissional completo dentro de uma plataforma confiável." },
  { icon: TrendingUp, title: "Geração de oportunidades", desc: "Conexões reais com famílias no momento certo da jornada." },
  { icon: Award, title: "Autoridade", desc: "Fortaleça sua reputação ao lado de uma marca de credibilidade." },
  { icon: BookOpen, title: "Conteúdo em parceria", desc: "Participe de conteúdos e amplie seu alcance." },
  { icon: Star, title: "Fornecedor fundador", desc: "Faça parte do grupo pioneiro que ajuda a construir a Maternnia." },
];

const EMPTY = {
  nome: "", empresa: "", categoria: "", cidade: "", bairro: "", email: "", whatsapp: "",
  site: "", instagram: "", registro: "", descricao: "", servicos: "", atendimento: "",
  publico: "", fundador: false, autorizacao: false, termos: false,
};

const inputClass =
  "w-full bg-white border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm text-maternnia-charcoal placeholder:text-maternnia-taupe/70 focus:outline-none focus:ring-2 focus:ring-maternnia-eucalipto/30 focus:border-maternnia-eucalipto transition";

const Field = ({ label, opcional, children, error, testid }) => (
  <div>
    <label className="block text-sm font-medium text-maternnia-charcoal">
      <span className="block mb-1.5">
        {label} {opcional && <span className="text-maternnia-taupe/70 font-light">(opcional)</span>}
      </span>
      {children}
    </label>
    {error && <p data-testid={testid} role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const FornecedoresPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Informe seu nome.";
    if (!form.empresa.trim()) e.empresa = "Informe o nome profissional ou da empresa.";
    if (!form.categoria) e.categoria = "Selecione uma categoria.";
    if (!form.cidade.trim()) e.cidade = "Informe a cidade.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Informe um e-mail válido.";
    if (!form.autorizacao) e.autorizacao = "Autorize o contato para continuar.";
    if (!form.termos) e.termos = "É necessário aceitar os termos.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setEnviando(true);
    try {
      await postJSON("/fornecedores", {
        nome: form.nome,
        empresa: form.empresa,
        categoria: form.categoria,
        atendimento: form.atendimento,
        cidade: form.cidade,
        bairro: form.bairro,
        email: form.email,
        whatsapp: form.whatsapp,
        site: form.site,
        instagram: form.instagram,
        registro: form.registro,
        publico: form.publico,
        descricao: form.descricao,
        servicos: form.servicos,
        fundador: form.fundador,
      });
      setSucesso(true);
      setForm(EMPTY);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Não foi possível enviar", { description: e.message });
    } finally {
      setEnviando(false);
    }
  };

  if (sucesso) {
    return (
      <main data-testid="fornecedores-page" className="bg-maternnia-porcelana/50 min-h-screen flex items-center">
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <motion.div data-testid="fornecedores-sucesso" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="bg-white rounded-[2rem] p-10 md:p-14 shadow-[0_24px_64px_rgba(43,37,38,0.10)]">
            <div className="h-16 w-16 mx-auto rounded-2xl bg-maternnia-eucalipto/10 flex items-center justify-center mb-6">
              <CheckCircle2 size={30} className="text-maternnia-eucalipto" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium">Cadastro recebido!</h1>
            <p className="mt-4 text-maternnia-taupe font-light leading-relaxed">
              Obrigado pelo interesse em fazer parte da rede Maternnia. Nossa equipe vai analisar seu cadastro e entrar em contato em breve. 🌿
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-maternnia-eucalipto text-white hover:bg-maternnia-floresta rounded-full px-7 py-3.5 text-sm font-medium transition-all duration-300 hover:scale-[1.03]">
              Voltar ao início <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="fornecedores-page" className="bg-white min-h-screen">
      <Seo
        title="Para fornecedores"
        path="/fornecedores"
        description="Profissionais, clínicas e serviços: faça parte da rede selecionada da Maternnia e conecte-se a famílias em cada etapa da maternidade."
      />
      {/* HERO */}
      <section className="bg-maternnia-eucalipto">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <span className="text-sm font-semibold tracking-widest text-white/70 uppercase">Para fornecedores</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-white mt-4 font-medium">
            Faça parte da rede Maternnia.
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/85 leading-relaxed font-light max-w-2xl">
            A Maternnia está selecionando profissionais, clínicas e serviços que valorizam qualidade, confiança e experiência humanizada.
          </p>
          <p className="mt-4 text-sm md:text-base text-white/70 leading-relaxed font-light max-w-2xl">
            Fazer parte da Maternnia significa atender a critérios de qualidade, ética e cuidado — e compartilhar dos nossos valores. Não é apenas pagar para aparecer: é integrar uma rede construída com curadoria.
          </p>
        </div>
      </section>

      {/* BENEFÍCIOS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium mb-10">Benefícios para parceiros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFICIOS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title} data-testid={`beneficio-${i}`}
                className="group bg-maternnia-porcelana rounded-3xl p-7 border border-maternnia-nevoa/60 shadow-sm hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(43,37,38,0.08)] transition-all duration-300"
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              >
                <div className="h-12 w-12 rounded-2xl bg-white flex items-center justify-center mb-5 group-hover:bg-maternnia-eucalipto transition-colors duration-300">
                  <Icon size={22} className="text-maternnia-eucalipto group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-xl text-maternnia-charcoal font-medium mb-1.5">{b.title}</h3>
                <p className="text-sm text-maternnia-taupe leading-relaxed font-light">{b.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FORM */}
      <section className="bg-maternnia-porcelana/50 py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-maternnia-charcoal font-medium text-center mb-3">Cadastre-se como fornecedor</h2>
          <p className="text-center text-sm text-maternnia-taupe font-light mb-10">Preencha seus dados e nossa equipe entrará em contato.</p>

          <form onSubmit={handleSubmit} data-testid="fornecedores-form" noValidate className="bg-white rounded-[2rem] p-7 md:p-10 shadow-[0_24px_64px_rgba(43,37,38,0.08)] space-y-5">
            {Object.keys(errors).length > 0 && (
              <div data-testid="fornecedores-erro" role="alert" aria-live="assertive" className="flex items-center gap-2.5 bg-red-50 text-red-600 rounded-xl px-4 py-3 text-sm">
                <AlertCircle size={16} /> Por favor, revise os campos destacados.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nome *" error={errors.nome} testid="forn-erro-nome">
                <input data-testid="forn-nome" type="text" value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Seu nome" className={inputClass} />
              </Field>
              <Field label="Nome profissional ou empresa *" error={errors.empresa} testid="forn-erro-empresa">
                <input data-testid="forn-empresa" type="text" value={form.empresa} onChange={(e) => set("empresa", e.target.value)} placeholder="Ex.: Clínica Vida" className={inputClass} />
              </Field>
              <Field label="Categoria *" error={errors.categoria} testid="forn-erro-categoria">
                <select data-testid="forn-categoria" value={form.categoria} onChange={(e) => set("categoria", e.target.value)} className={`${inputClass} ${form.categoria === "" ? "text-maternnia-taupe/70" : ""}`}>
                  <option value="" disabled>Selecione</option>
                  {[...new Set([...CATEGORIAS_FILTRO, "Maternidades", "Fotografia", "Outro"])].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Atendimento" opcional>
                <select data-testid="forn-atendimento" value={form.atendimento} onChange={(e) => set("atendimento", e.target.value)} className={`${inputClass} ${form.atendimento === "" ? "text-maternnia-taupe/70" : ""}`}>
                  <option value="">Selecione</option>
                  <option>Presencial</option>
                  <option>Online</option>
                  <option>Presencial e online</option>
                </select>
              </Field>
              <Field label="Cidade *" error={errors.cidade} testid="forn-erro-cidade">
                <input data-testid="forn-cidade" type="text" value={form.cidade} onChange={(e) => set("cidade", e.target.value)} placeholder="Cidade" className={inputClass} />
              </Field>
              <Field label="Bairro" opcional>
                <input data-testid="forn-bairro" type="text" value={form.bairro} onChange={(e) => set("bairro", e.target.value)} placeholder="Bairro" className={inputClass} />
              </Field>
              <Field label="E-mail *" error={errors.email} testid="forn-erro-email">
                <input data-testid="forn-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="contato@email.com" className={inputClass} />
              </Field>
              <Field label="WhatsApp" opcional>
                <input data-testid="forn-whatsapp" type="tel" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="(11) 9 0000-0000" className={inputClass} />
              </Field>
              <Field label="Site" opcional>
                <input data-testid="forn-site" type="text" value={form.site} onChange={(e) => set("site", e.target.value)} placeholder="www.seusite.com.br" className={inputClass} />
              </Field>
              <Field label="Instagram" opcional>
                <input data-testid="forn-instagram" type="text" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@seuperfil" className={inputClass} />
              </Field>
              <Field label="Registro profissional" opcional>
                <input data-testid="forn-registro" type="text" value={form.registro} onChange={(e) => set("registro", e.target.value)} placeholder="CRM, CRN, CREFITO... (quando aplicável)" className={inputClass} />
              </Field>
              <Field label="Público atendido" opcional>
                <input data-testid="forn-publico" type="text" value={form.publico} onChange={(e) => set("publico", e.target.value)} placeholder="Ex.: gestantes, recém-nascidos..." className={inputClass} />
              </Field>
            </div>

            <Field label="Descrição" opcional>
              <textarea data-testid="forn-descricao" rows={3} value={form.descricao} onChange={(e) => set("descricao", e.target.value)} placeholder="Conte sobre você ou sua empresa..." className={`${inputClass} resize-none`} />
            </Field>
            <Field label="Serviços oferecidos" opcional>
              <textarea data-testid="forn-servicos" rows={2} value={form.servicos} onChange={(e) => set("servicos", e.target.value)} placeholder="Liste seus principais serviços..." className={`${inputClass} resize-none`} />
            </Field>

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="forn-fundador" type="checkbox" checked={form.fundador} onChange={(e) => set("fundador", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-maternnia-nevoa accent-maternnia-eucalipto shrink-0" />
                <span className="text-sm text-maternnia-charcoal font-light">Tenho interesse em participar como <strong className="font-medium">fornecedor fundador</strong>.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="forn-autorizacao" type="checkbox" checked={form.autorizacao} onChange={(e) => set("autorizacao", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-maternnia-nevoa accent-maternnia-eucalipto shrink-0" />
                <span className="text-xs text-maternnia-taupe font-light leading-relaxed">Autorizo a Maternnia a entrar em contato comigo sobre esta solicitação. *</span>
              </label>
              {errors.autorizacao && <p data-testid="forn-erro-autorizacao" className="text-xs text-red-500 -mt-1">{errors.autorizacao}</p>}
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="forn-termos" type="checkbox" checked={form.termos} onChange={(e) => set("termos", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-maternnia-nevoa accent-maternnia-eucalipto shrink-0" />
                <span className="text-xs text-maternnia-taupe font-light leading-relaxed">Li e aceito os{" "}
                  <Link to="/termos" className="text-maternnia-eucalipto underline underline-offset-2 hover:text-maternnia-floresta">termos de uso</Link>{" "}e a{" "}
                  <Link to="/privacidade" className="text-maternnia-eucalipto underline underline-offset-2 hover:text-maternnia-floresta">política de privacidade</Link>. *
                </span>
              </label>
              {errors.termos && <p data-testid="forn-erro-termos" className="text-xs text-red-500 -mt-1">{errors.termos}</p>}
            </div>

            <button
              type="submit" data-testid="forn-submit" disabled={enviando}
              className="group w-full inline-flex items-center justify-center gap-2 bg-maternnia-eucalipto text-white hover:bg-maternnia-floresta rounded-full px-8 py-4 text-base font-medium transition-all duration-300 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {enviando ? (<><Loader2 size={18} className="animate-spin" /> Enviando...</>) : (<>Quero participar <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></>)}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default FornecedoresPage;
